/**
 * 设置 · 量具与选项（v0.61，**仅管理员可改**）。
 *
 * ★ 用户的原始诉求（2026-10-17）：
 *   「测量工具我需要增加量具只需要到后台增加，前面得气泡图跟指导书页面得测量工具
 *     都会跟着添加；一些基本定型了得结构增加设置来调整，免去每次都要发信息给你」
 *
 *   所以这一页的价值不在"能编辑"，而在**一处改、三处跟着变**：
 *     量具清单 → 气泡图页「检测工具」下拉 / 指导书页「量具·方法」下拉 /
 *                测量报告 F 列 + 模板的数据验证
 *
 * ★ 「仅管理员」在这里只体现为**入口不渲染**（App 里按 role 判）。
 *   真正的拦截在后端权限矩阵（`POST/PATCH/DELETE /api/settings...` → admin）。
 *   前端藏按钮不是安全边界 —— 但能把"点了才知道不行"变成"根本点不到"。
 *
 * ⚠️ 界面上不做的事：
 *   · 不提供"一键清空"—— 一个能悄悄删掉用户数据的按钮比没有更危险；
 *   · 「恢复出厂」只补齐/归正**预置项**，从不删用户自己加的行（后端保证）；
 *   · 删除还有业务数据在用的值要**二次确认**（后端 409 带 `used_by` 行数）。
 */
import { useEffect, useMemo, useState } from 'react';
import {
  createOption, createOptionsBatch, deleteOption, getOptions, patchOption,
  reorderOptions, resetOptions,
} from '../api/client';
import { OPTION_KINDS, useOptionsStore, type OptionKind } from '../store/options';
import type { OptionItem } from '../types';

interface Props {
  onToast: (msg: string, type?: 'info' | 'success' | 'error') => void;
}

/** 每一类「拿来干什么」——**展示文案**，不是业务语义，所以放前端。 */
const KIND_USE: Record<OptionKind, string> = {
  method: '气泡图页「检测工具」+ 指导书页「量具/方法」+ 测量报告「测量设备」列',
  freq: '指导书页「检验频率」',
  customer: '零件主数据「客户」',
  project_type: '零件主数据「项目类型」',
  mold_type: '零件主数据「模具类型」',
  cavity_count: '零件主数据「模穴」',
  press_tonnage: '零件主数据「冲床吨位」',
  sip_kind: '作业指导书（SIP 明细）「类别」列',
};

/** 只有检测工具有「报告落点」与「小数位」两栏（后端也只允许它填）。 */
const hasReportCols = (k: OptionKind) => k === 'method';

export function SettingsPage({ onToast }: Props) {
  const items = useOptionsStore((s) => s.items);
  const kindCn = useOptionsStore((s) => s.kindCn);
  const load = useOptionsStore((s) => s.load);
  const applyResponse = useOptionsStore((s) => s.applyResponse);
  const applyItem = useOptionsStore((s) => s.applyItem);
  const removeItem = useOptionsStore((s) => s.removeItem);

  const [kind, setKind] = useState<OptionKind>('method');
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState('');
  const [draftReport, setDraftReport] = useState('');
  const [draftPrecision, setDraftPrecision] = useState('');
  const [batchOpen, setBatchOpen] = useState(false);
  const [batchText, setBatchText] = useState('');

  const reload = async () => {
    // ★ 走 store 的**唯一入口**做权威刷新 —— 不在这里自己 merge。
    //   曾经写过一版：页面里手搓一份 values 合并 + degraded 判定，与 store
    //   启动加载那份逻辑并存 ⇒ 同一语义两份实现（本项目头号坑）。
    applyResponse(await getOptions());
  };

  useEffect(() => { void load(); }, [load]);

  const rows = useMemo(
    () => [...(items[kind] ?? [])].sort((a, b) => a.sort_order - b.sort_order || a.id - b.id),
    [items, kind]);

  const kindLabel = (k: OptionKind) => kindCn[k] || k;

  const guard = async (fn: () => Promise<void>) => {
    if (busy) return;
    setBusy(true);
    try { await fn(); } catch (e: any) {
      onToast(e?.message || '操作失败', 'error');
    } finally { setBusy(false); }
  };

  const doAdd = () => guard(async () => {
    const v = draft.trim();
    if (!v) { onToast('先填要加的值', 'error'); return; }
    const prec = draftPrecision.trim();
    if (prec !== '' && !(Number.isInteger(Number(prec)) && Number(prec) >= 0 && Number(prec) <= 6)) {
      onToast('小数位只能是 0–6 的整数（0 = ✓/✗ 定性）', 'error');
      return;
    }
    const it = await createOption({
      kind, value: v,
      report_name: hasReportCols(kind) ? (draftReport.trim() || null) : null,
      precision: (hasReportCols(kind) && prec !== '') ? Number(prec) : null,
    });
    applyItem(it);
    setDraft(''); setDraftReport(''); setDraftPrecision('');
    onToast(`已加「${it.value}」—— 气泡图 / 指导书 / 测量报告三处同时生效`, 'success');
  });

  const doBatch = () => guard(async () => {
    const vals = batchText.split(/[\n,，、;；]+/).map((s) => s.trim()).filter(Boolean);
    if (!vals.length) { onToast('没有可加的值', 'error'); return; }
    const added = await createOptionsBatch(kind, vals);
    added.forEach(applyItem);
    setBatchText(''); setBatchOpen(false);
    onToast(`已加 ${added.length} 项${added.length < vals.length
      ? `（${vals.length - added.length} 项已存在，未重复添加）` : ''}`, 'success');
  });

  const doPatch = (it: OptionItem, body: Parameters<typeof patchOption>[1]) =>
    guard(async () => {
      const next = await patchOption(it.id, body);
      applyItem(next);
    });

  const doDelete = (it: OptionItem) => guard(async () => {
    try {
      const r = await deleteOption(it.id, false);
      removeItem(kind, it.id);
      onToast(`已删除「${r.value || it.value}」`, 'success');
    } catch (e: any) {
      const msg = String(e?.message || '');
      // 后端 409 会把"还有几行在用"说清楚 —— 让用户看着真实数字决定
      if (!/还有|在用|used/i.test(msg)) { onToast(msg, 'error'); return; }
      if (!window.confirm(`${msg}\n\n仍要删除吗？\n（历史数据不会被改写，但以后新行再也选不到它）`)) {
        return;
      }
      const r = await deleteOption(it.id, true);
      removeItem(kind, it.id);
      onToast(`已强制删除「${r.value || it.value}」`, 'success');
    }
  });

  const doMove = (i: number, delta: number) => guard(async () => {
    const ids = rows.map((r) => r.id);
    const j = i + delta;
    if (j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    const out = await reorderOptions(kind, ids);
    out.forEach(applyItem);
  });

  const doReset = () => guard(async () => {
    if (!window.confirm(
      `把「${kindLabel(kind)}」的**出厂预置项**补齐并归正？\n\n`
      + '· 你自己加的行不受影响（不会被删）\n'
      + '· 停用的预置项会被重新启用')) return;
    const r = await resetOptions(kind);
    await reload();
    onToast(`已恢复出厂：补齐 ${r.added} 项、归正 ${r.fixed} 项`, 'success');
  });

  return (
    <div className="pm-page set-page">
      <div className="pm-head">
        <h2>设置 · 量具与选项</h2>
        <div className="pm-head-actions">
          <span className="set-hint-inline">
            这里改一次，气泡图 / 指导书 / 测量报告同时生效
          </span>
        </div>
      </div>

      <div className="set-body">
        {/* ---- 左：八类 ---- */}
        <nav className="set-tabs">
          {OPTION_KINDS.map((k) => {
            const n = (items[k] ?? []).length;
            return (
              <button key={k}
                      className={`set-tab${k === kind ? ' on' : ''}`}
                      onClick={() => setKind(k)}
                      title={KIND_USE[k]}>
                <b>{kindLabel(k)}</b>
                <span className="set-tab-n">{n || '空'}</span>
              </button>
            );
          })}
        </nav>

        {/* ---- 右：该类明细 ---- */}
        <section className="set-main">
          <div className="set-use">
            {kindLabel(kind)} —— 用在：{KIND_USE[kind]}
            {hasReportCols(kind) && (
              <span className="set-use-sub">
                　「报告落点」= 写进测量报告「测量设备」列的名字；留空则原样写下拉值。
                模板 F 列的数据验证会按本清单**自动重写**，所以新加的量具不会让 Excel 弹框。
              </span>
            )}
            {kind === 'sip_kind' && (
              <span className="set-use-sub">
                　前 5 项是识别链路的内部枚举（界面显示中文）；后面新加的项<b>原样显示</b>，
                直接写中文即可（如「外观」「包装」）。停用只影响下拉，<b>不改历史行</b>。
              </span>
            )}
          </div>

          {/* 新增 */}
          <div className="set-add">
            <input className="pm-input" placeholder={`新增一项${kindLabel(kind)}的值`}
                   value={draft} onChange={(e) => setDraft(e.target.value)}
                   onKeyDown={(e) => { if (e.key === 'Enter') doAdd(); }} />
            {hasReportCols(kind) && (
              <>
                <input className="pm-input sm" placeholder="报告落点（可空）"
                       value={draftReport}
                       onChange={(e) => setDraftReport(e.target.value)} />
                <input className="pm-input sm" placeholder="小数位 0-6（可空）"
                       value={draftPrecision}
                       onChange={(e) => setDraftPrecision(e.target.value)} />
              </>
            )}
            <button className="btn primary" disabled={busy} onClick={doAdd}>新增</button>
            <button className="btn" disabled={busy}
                    onClick={() => setBatchOpen((o) => !o)}>
              批量粘贴
            </button>
            <button className="btn" disabled={busy} onClick={doReset}
                    title="补齐/归正出厂预置项；不会删除你自己加的行">
              恢复出厂
            </button>
          </div>

          {batchOpen && (
            <div className="set-batch">
              <textarea className="pm-input" rows={4}
                        placeholder="一行一个，或用逗号分隔。例：内径千分表，深度尺，高度表"
                        value={batchText}
                        onChange={(e) => setBatchText(e.target.value)} />
              <div className="set-batch-bar">
                <span className="set-hint-inline">
                  单条失败**整批回滚** —— 不会出现"前几条进去了、中间一条没进"。
                </span>
                <button className="btn primary" disabled={busy} onClick={doBatch}>加入</button>
                <button className="btn" disabled={busy}
                        onClick={() => { setBatchOpen(false); setBatchText(''); }}>取消</button>
              </div>
            </div>
          )}

          {/* 明细 */}
          {rows.length === 0 ? (
            <div className="ua-empty">
              这一类还是空的 —— 在上面「新增」里加第一项。
              {['mold_type', 'cavity_count', 'press_tonnage'].includes(kind) && (
                <div className="set-empty-note">
                  为什么是空的：这三类是**你厂的工艺参数**，系统没有任何依据替你编，
                  所以出厂不预置。录一次以后就一直有了。
                </div>
              )}
            </div>
          ) : (
            <div className="set-tablebox">
              <table className="set-table">
                <thead>
                  <tr>
                    <th style={{ width: 46 }}>序</th>
                    <th>值</th>
                    {hasReportCols(kind) && <th style={{ width: 140 }}>报告落点</th>}
                    {hasReportCols(kind) && <th style={{ width: 74 }}>小数位</th>}
                    <th style={{ width: 190 }}>备注</th>
                    <th style={{ width: 74 }}>启用</th>
                    <th style={{ width: 150 }}>操作</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((it, i) => (
                    <SetRow key={it.id} it={it} index={i} total={rows.length}
                            busy={busy} showReport={hasReportCols(kind)}
                            onPatch={(b) => doPatch(it, b)}
                            onDelete={() => doDelete(it)}
                            onMove={(d) => doMove(i, d)} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="set-foot">
            停用的项：下拉里看不到，但**历史数据行照旧显示**（不会被改成别的值）。
            删除还在用的值会二次确认并告诉你有几行在用。
          </div>
        </section>
      </div>
    </div>
  );
}

/** 单行编辑。`value` / `report_name` / `note` 用**失焦或回车**提交，避免每键一请求。 */
function SetRow({ it, index, total, busy, showReport, onPatch, onDelete, onMove }: {
  it: OptionItem; index: number; total: number; busy: boolean; showReport: boolean;
  onPatch: (b: Parameters<typeof patchOption>[1]) => void;
  onDelete: () => void;
  onMove: (delta: number) => void;
}) {
  const [value, setValue] = useState(it.value);
  const [report, setReport] = useState(it.report_name ?? '');
  const [prec, setPrec] = useState(it.precision == null ? '' : String(it.precision));
  const [note, setNote] = useState(it.note ?? '');

  // 外部（批量操作 / 恢复出厂）改了这条 ⇒ 同步进本地输入框
  useEffect(() => { setValue(it.value); }, [it.value]);
  useEffect(() => { setReport(it.report_name ?? ''); }, [it.report_name]);
  useEffect(() => { setPrec(it.precision == null ? '' : String(it.precision)); }, [it.precision]);
  useEffect(() => { setNote(it.note ?? ''); }, [it.note]);

  const commitValue = () => {
    const v = value.trim();
    if (!v) { setValue(it.value); return; }      // 不许改成空值（那会毁掉历史行的对应）
    if (v !== it.value) onPatch({ value: v });
  };
  const commitReport = () => {
    const v = report.trim();
    if (v !== (it.report_name ?? '')) onPatch({ report_name: v || null });
  };
  const commitPrec = () => {
    const v = prec.trim();
    const cur = it.precision == null ? '' : String(it.precision);
    if (v === cur) return;
    if (v === '') { onPatch({ precision: null }); return; }
    const n = Number(v);
    if (!(Number.isInteger(n) && n >= 0 && n <= 6)) { setPrec(cur); return; }
    onPatch({ precision: n });
  };
  const commitNote = () => {
    if (note !== (it.note ?? '')) onPatch({ note });
  };

  return (
    <tr className={it.enabled ? '' : 'set-off'}>
      <td className="set-idx">
        <button className="set-mv" disabled={busy || index === 0}
                title="上移" onClick={() => onMove(-1)}>↑</button>
        <button className="set-mv" disabled={busy || index === total - 1}
                title="下移" onClick={() => onMove(1)}>↓</button>
      </td>
      <td>
        <input className="pm-input" value={value} disabled={busy}
               onChange={(e) => setValue(e.target.value)}
               onBlur={commitValue}
               onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }} />
        {it.builtin && <em className="pm-key" title="出厂预置项">预置</em>}
      </td>
      {showReport && (
        <td>
          <input className="pm-input" value={report} disabled={busy}
                 placeholder={`同值（${it.value}）`}
                 onChange={(e) => setReport(e.target.value)}
                 onBlur={commitReport}
                 onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }} />
        </td>
      )}
      {showReport && (
        <td>
          <input className="pm-input sm" value={prec} disabled={busy}
                 placeholder="—"
                 onChange={(e) => setPrec(e.target.value)}
                 onBlur={commitPrec}
                 onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }} />
        </td>
      )}
      <td>
        <input className="pm-input" value={note} disabled={busy} placeholder="—"
               onChange={(e) => setNote(e.target.value)}
               onBlur={commitNote}
               onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }} />
      </td>
      <td>
        <input type="checkbox" className="ua-check" checked={it.enabled} disabled={busy}
               title={it.enabled ? '停用后下拉里不再出现（历史行不受影响）' : '启用'}
               onChange={(e) => onPatch({ enabled: e.target.checked })} />
      </td>
      <td className="set-ops">
        <button className="btn sm btn-danger" disabled={busy} onClick={onDelete}>删除</button>
      </td>
    </tr>
  );
}
